import React, { useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
  CircularProgress,
  Alert,
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Label,
} from "recharts";
import {
  Star,
  TrendingUp,
  TrendingDown,
  PieChart as PieIcon,
  BarChart as BarIconMUI,
  WarningAmberOutlined as WarningIcon,
  AccountBalanceWalletOutlined as BalanceIcon,
  AttachMoney,
  Assessment,
  ShowChart as LineStyleIcon,
  GroupWork as GroupIcon,
  ErrorOutline,
  EmojiEvents,
  InfoOutlined as InfoIcon,
  NewspaperOutlined as NewsIcon,
  Insights as InsightsIcon,
  Category as CategoryIcon,
  SignalCellularAlt as SignalIcon,
  PriceCheck as PriceCheckIcon,
  CardGiftcard as GiftIcon,
  ReportProblemOutlined as ExclamationTriangleIcon,
  Compress as CompressIcon,
  FilterList as FilterListIcon,
  ArrowDownwardSharp as MaxDrawdownIcon,
} from "@mui/icons-material";
import Utils from "../util/Utils";
import { API_KEYS, FMP_API_ENDPOINTS } from "../util/Constants";

// --- Helper functions & constants ---
const SECTOR_COLORS = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#C9CBCF",
  "#77DD77",
  "#B0E0E6",
  "#FFD700",
  "#FF7F50",
  "#DA70D6",
  "#DB7093",
  "#6495ED",
  "#DC143C",
];
const PE_COLORS = ["#2ecc40", "#ffdc00", "#ff851b", "#ff4136", "#800080"];

const lightenColor = (color, percent) => {
  let num = parseInt(color.replace("#", ""), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    B = ((num >> 8) & 0x00ff) + amt,
    G = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
      (G < 255 ? (G < 1 ? 0 : G) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

// Removed formatPercentLocal, will use Utils.format_ratio directly

const getSectorHHI = (data) => {
  const total = data.reduce((sum, s) => sum + (s.marketCap || 0), 0);
  if (total === 0) return 0;
  const sectorShares = {};
  data.forEach((s) => {
    const sector = s.sector || "Unassigned";
    sectorShares[sector] = (sectorShares[sector] || 0) + (s.marketCap || 0);
  });
  return Object.values(sectorShares)
    .map((v) => v / total)
    .reduce((sum, share) => sum + share * share, 0);
};

const getTopHoldingsExposure = (data, topN = 5) => {
  const total = data.reduce((sum, s) => sum + (s.marketCap || 0), 0);
  if (total === 0) return 0;
  const sorted = [...data]
    .filter((s) => s.marketCap && s.marketCap > 0)
    .sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0));
  const topSum = sorted
    .slice(0, topN)
    .reduce((sum, s) => sum + (s.marketCap || 0), 0);
  return total === 0 ? 0 : topSum / total;
};

const getTopContributors = (data, field, topN = 3) => {
  const sorted = [...data]
    .filter((s) => typeof s[field] === "number" && !isNaN(s[field]))
    .sort((a, b) => (b[field] || 0) - (a[field] || 0));
  return sorted.slice(0, topN);
};

const getBottomContributors = (data, field, topN = 3) => {
  const sorted = [...data]
    .filter((s) => typeof s[field] === "number" && !isNaN(s[field]))
    .sort((a, b) => (a[field] || 0) - (b[field] || 0));
  return sorted.slice(0, topN);
};

// getVolatility function removed as it's no longer used for insights

const getMaxDrawdown = (data, perfField = "perfYear") => {
  let maxDrawdownValue = 0;
  let stockSymbol = null;
  data.forEach((s) => {
    const perfValue = Number(s[perfField]);
    if (
      s[perfField] !== undefined &&
      !isNaN(perfValue) &&
      perfValue < maxDrawdownValue
    ) {
      maxDrawdownValue = perfValue;
      stockSymbol = s.symbol;
    }
  });
  return { value: maxDrawdownValue, symbol: stockSymbol };
};

const peBucketsData = (portfolioAnalysisData) => {
  const peBuckets = { "<10": 0, "10-20": 0, "20-30": 0, "30-50": 0, "50+": 0 };
  portfolioAnalysisData.forEach((s) => {
    const pe = Number(s.pe);
    if (isNaN(pe)) return;
    if (pe < 10) peBuckets["<10"]++;
    else if (pe < 20) peBuckets["10-20"]++;
    else if (pe < 30) peBuckets["20-30"]++;
    else if (pe < 50) peBuckets["30-50"]++;
    else if (pe >= 50) peBuckets["50+"]++;
  });
  return Object.entries(peBuckets).map(([name, value], i) => ({
    name,
    value,
    fill: PE_COLORS[i % PE_COLORS.length],
  }));
};

const iconMapMUI = {
  "pie chart": <PieIcon color="primary" />,
  "chart bar outline": <BarIconMUI color="secondary" />,
  "building outline": <GroupIcon color="action" />,
  compress: <CompressIcon color="info" />,
  bullseye: <FilterListIcon color="warning" />,
  "arrow up": <TrendingUp color="success" />,
  "arrow down": <TrendingDown color="error" />,
  "chart line": <LineStyleIcon color="info" />,
  trophy: <EmojiEvents sx={{ color: "goldenrod" }} />,
  "thumbs down outline": <WarningIcon color="error" />,
  "balance scale right": <BalanceIcon color="success" />,
  "balance scale left": <BalanceIcon sx={{ color: "orange" }} />,
  "chart area": <BarIconMUI color="primary" />,
  "dollar sign": <AttachMoney color="success" />,
  "gem outline": <Star sx={{ color: "purple" }} />,
  // "line graph": <VolatilityIcon color="info" />, // Volatility icon removed
  "angle double down": <MaxDrawdownIcon color="error" />,
  "exclamation triangle": <ExclamationTriangleIcon color="error" />,
  "exclamation circle": <ErrorOutline color="warning" />,
  "info circle": <InfoIcon color="info" />,
  newspaper: <NewsIcon color="action" />,
  cubes: <CategoryIcon color="action" />,
  signal: <SignalIcon color="info" />,
  "search dollar": <PriceCheckIcon color="success" />,
  gift: <GiftIcon color="secondary" />,
  "warning sign": <WarningIcon color="error" />,
};

const renderSectorLabel = (props) => {
  const { cx, cy, midAngle, outerRadius, index, name, value, percent, fill } =
    props;
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + 28;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const sin = Math.sin(-midAngle * RADIAN);
  const cos = Math.cos(-midAngle * RADIAN);
  const sx = cx + (outerRadius + 8) * cos;
  const sy = cy + (outerRadius + 8) * sin;
  const mx = cx + (outerRadius + 20) * cos;
  const my = cy + (outerRadius + 20) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 15;
  const ey = my;
  const textAnchor = cos >= 0 ? "start" : "end";

  if (percent * 100 < 2) return null;

  return (
    <g>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2.5} fill={fill} stroke="none" />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 5}
        y={ey}
        textAnchor={textAnchor}
        fill="#444"
        dominantBaseline="central"
        fontSize="11px"
        fontWeight="500"
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    </g>
  );
};

const ScreenerInisghts = ({
  name,
  portfolioAnalysisData,
  symbolDistributionData,
  formatDollarValue,
  renderCustomizedLabelWithLines,
  BASE_COLORS,
  lightenColor,
}) => {
  const portfolioName = name;

  const sectorAllocationByCount = useMemo(() => {
    if (!portfolioAnalysisData || portfolioAnalysisData.length === 0) return [];
    const allocation = portfolioAnalysisData.reduce((acc, stock) => {
      const sector = stock.sector || "Unassigned";
      acc[sector] = (acc[sector] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(allocation)
      .map(([name, value]) => ({ name, value }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [portfolioAnalysisData]);

  const insightsByCategory = useMemo(() => {
    if (!portfolioAnalysisData || portfolioAnalysisData.length === 0)
      return {
        composition: [],
        shortTermPerformance: [],
        longTermPerformance: [],
        valuation: [],
        dividends: [],
      };

    const insights = {
      composition: [],
      shortTermPerformance: [],
      longTermPerformance: [],
      valuation: [],
      dividends: [],
    };
    const totalScreenedStocks = portfolioAnalysisData.length;

    // --- Composition Insights ---
    if (sectorAllocationByCount.length > 0) {
      const topSector = sectorAllocationByCount[0];
      const topSectorPercentage = (topSector.value / totalScreenedStocks) * 100;
      insights.composition.push({
        key: "sector_concentration_top",
        text: `Highest concentration in ${
          topSector.name
        } sector (${topSectorPercentage.toFixed(0)}% - ${
          topSector.value
        } stocks).`,
        iconName: "pie chart",
        colorProp: "primary",
      });
      if (sectorAllocationByCount.length > 1) {
        const secondSector = sectorAllocationByCount[1];
        const secondSectorPercentage =
          (secondSector.value / totalScreenedStocks) * 100;
        insights.composition.push({
          key: "sector_concentration_second",
          text: `Second largest is ${
            secondSector.name
          } with ${secondSectorPercentage.toFixed(0)}% (${
            secondSector.value
          } stocks).`,
          iconName: "chart bar outline",
          colorProp: "secondary",
        });
      }
    }

    const sizeCounts = portfolioAnalysisData.reduce((acc, stock) => {
      const size = stock.companySize || "N/A";
      acc[size] = (acc[size] || 0) + 1;
      return acc;
    }, {});
    const sortedSizes = Object.entries(sizeCounts).sort(
      ([, a], [, b]) => b - a
    );
    if (sortedSizes.length > 0) {
      const dominantSize = sortedSizes[0][0];
      insights.composition.push({
        key: "company_size",
        text: `Majority of the companies in this portfolio are ${dominantSize}-cap.`,
        iconName: "building outline",
        colorProp: "info",
      });
    }

    const sectorHHI = getSectorHHI(portfolioAnalysisData);
    insights.composition.push({
      key: "sector_hhi",
      text: `Sector diversification Score is ${sectorHHI.toFixed(
        2
      )}. Lower scores (e.g., <0.15) indicate better diversification.`,
      iconName: "compress",
      colorProp:
        sectorHHI > 0.25 ? "error" : sectorHHI > 0.18 ? "warning" : "success",
    });

    const top5Exposure = getTopHoldingsExposure(portfolioAnalysisData, 5);
    insights.composition.push({
      key: "top5_exposure",
      text: `Top 5 holdings by market cap represent ${(
        top5Exposure * 100
      ).toFixed(1)}% of this selection's total market cap.`,
      iconName: "bullseye",
      colorProp:
        top5Exposure > 0.5
          ? "error"
          : top5Exposure > 0.3
          ? "warning"
          : "success",
    });

    // --- Short Term Performance Insights ---
    if (portfolioAnalysisData && portfolioAnalysisData.length > 0) {
      // Weekly Top Performers
      const weeklyPerformers = getTopContributors(
        portfolioAnalysisData,
        "perfWeek",
        3
      );
      if (weeklyPerformers.length > 0) {
        insights.shortTermPerformance.push({
          key: "weekly_top_performers",
          text: `Top weekly performers: ${weeklyPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfWeek)})`)
            .join(", ")}.`,
          iconName: "arrow up",
          colorProp: "success",
        });
      }

      // Monthly Top Performers
      const monthlyPerformers = getTopContributors(
        portfolioAnalysisData,
        "perfMonth",
        3
      );
      if (monthlyPerformers.length > 0) {
        insights.shortTermPerformance.push({
          key: "monthly_top_performers",
          text: `Top monthly performers: ${monthlyPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfMonth)})`)
            .join(", ")}.`,
          iconName: "chart line",
          colorProp: "primary",
        });
      }

      // YTD Top Performers
      const ytdPerformers = getTopContributors(
        portfolioAnalysisData,
        "perfYTD",
        3
      );
      if (ytdPerformers.length > 0) {
        insights.shortTermPerformance.push({
          key: "ytd_top_performers",
          text: `Top YTD performers: ${ytdPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfYTD)})`)
            .join(", ")}.`,
          iconName: "trophy",
          colorProp: "warning",
        });
      }

      // Add divider between positive and negative insights
      if (insights.shortTermPerformance.length > 0) {
        insights.shortTermPerformance.push({
          key: "divider",
          text: "Areas of Concern",
          colorProp: "error",
          isDivider: true,
        });
      }

      // Weekly Warnings
      const weeklyLosers = getBottomContributors(
        portfolioAnalysisData,
        "perfWeek",
        3
      );
      const significantWeeklyLosses = weeklyLosers.filter(
        (s) => s.perfWeek < -0.05
      );
      if (significantWeeklyLosses.length > 0) {
        insights.shortTermPerformance.push({
          key: "weekly_significant_losses",
          text: `Warning: ${
            significantWeeklyLosses.length
          } stocks down more than 5% this week: ${significantWeeklyLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfWeek)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }

      // Monthly Warnings
      const monthlyLosers = getBottomContributors(
        portfolioAnalysisData,
        "perfMonth",
        3
      );
      const significantMonthlyLosses = monthlyLosers.filter(
        (s) => s.perfMonth < -0.1
      );
      if (significantMonthlyLosses.length > 0) {
        insights.shortTermPerformance.push({
          key: "monthly_significant_losses",
          text: `Warning: ${
            significantMonthlyLosses.length
          } stocks down more than 10% this month: ${significantMonthlyLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfMonth)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }
    }

    // --- Long Term Performance Insights ---
    if (portfolioAnalysisData && portfolioAnalysisData.length > 0) {
      // 1-Year Top Performers
      const yearPerformers = getTopContributors(
        portfolioAnalysisData,
        "perfYear",
        3
      );
      if (yearPerformers.length > 0) {
        insights.longTermPerformance.push({
          key: "year_top_performers",
          text: `Top 1-year performers: ${yearPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfYear)})`)
            .join(", ")}.`,
          iconName: "chart line",
          colorProp: "primary",
        });
      }

      // 5Y (annualized) top performers
      const fiveYearPerformers = getTopContributors(
        portfolioAnalysisData,
        "perf5Year",
        3
      );
      if (fiveYearPerformers.length > 0) {
        insights.longTermPerformance.push({
          key: "five_year_top_performers",
          text: `Top 5Y (annualized) performers: ${fiveYearPerformers
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perf5Year)})`)
            .join(", ")}.`,
          iconName: "trophy",
          colorProp: "warning",
        });
      }

      // Add divider between positive and negative insights
      if (insights.longTermPerformance.length > 0) {
        insights.longTermPerformance.push({
          key: "divider",
          text: "Areas of Concern",
          colorProp: "error",
          isDivider: true,
        });
      }

      // 1-Year Warnings
      const yearLosers = getBottomContributors(
        portfolioAnalysisData,
        "perfYear",
        3
      );
      const significantYearLosses = yearLosers.filter((s) => s.perfYear < -0.2);
      if (significantYearLosses.length > 0) {
        insights.longTermPerformance.push({
          key: "year_significant_losses",
          text: `Warning: ${
            significantYearLosses.length
          } stocks down more than 20% over the past year: ${significantYearLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perfYear)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }

      // 5Y (annualized) warnings
      const fiveYearLosers = getBottomContributors(
        portfolioAnalysisData,
        "perf5Year",
        3
      );
      const significantFiveYearLosses = fiveYearLosers.filter(
        (s) => s.perf5Year < -0.3
      );
      if (significantFiveYearLosses.length > 0) {
        insights.longTermPerformance.push({
          key: "five_year_significant_losses",
          text: `Warning: ${
            significantFiveYearLosses.length
          } stocks with 5Y annualized return below -30%: ${significantFiveYearLosses
            .map((s) => `${s.symbol} (${Utils.format_ratio(s.perf5Year)})`)
            .join(", ")}.`,
          iconName: "warning sign",
          colorProp: "error",
        });
      }
    }

    // --- Valuation & Financial Health Insights ---
    if (portfolioAnalysisData && portfolioAnalysisData.length > 0) {
      // Valuation Analysis
      const avgPE =
        portfolioAnalysisData.reduce(
          (sum, stock) => sum + (Number(stock.pe) || 0),
          0
        ) /
        portfolioAnalysisData.filter((stock) => stock.pe && !isNaN(stock.pe))
          .length;

      if (!isNaN(avgPE)) {
        insights.valuation.push({
          key: "avg_pe",
          text: `Average P/E ratio of ${avgPE.toFixed(1)}x, suggesting ${
            avgPE < 15
              ? "potentially undervalued"
              : avgPE < 25
              ? "fairly valued"
              : "potentially overvalued"
          } portfolio.`,
          iconName: "chart area",
          colorProp: avgPE < 15 ? "success" : avgPE < 25 ? "info" : "warning",
        });
      }

      // Add divider between positive and negative insights
      if (insights.valuation.length > 0) {
        insights.valuation.push({
          key: "divider",
          text: "Areas of Concern",
          colorProp: "error",
          isDivider: true,
        });
      }

      // Risk Analysis - Keep only extreme valuation warnings
      const extremeValuationStocks = portfolioAnalysisData.filter(
        (stock) => stock.pe >= 100
      );
      if (extremeValuationStocks.length > 0) {
        insights.valuation.push({
          key: "extreme_valuation_warning",
          text: `${extremeValuationStocks.length} stocks have extreme P/E ratios (>100x), suggesting speculative valuations.`,
          iconName: "exclamation triangle",
          colorProp: "error",
        });
      }
    }

    // --- Dividend Insights ---
    const dividendPayers = portfolioAnalysisData.filter(
      (s) => (Number(s.dividendYield) || 0) > 0
    );
    if (dividendPayers.length > 0) {
      const avgDividendYield =
        dividendPayers.reduce(
          (sum, s) => sum + (Number(s.dividendYield) || 0),
          0
        ) / dividendPayers.length;
      insights.dividends.push({
        key: "dividend_payers",
        text: `${
          dividendPayers.length
        } stocks pay a dividend. Average yield: ${Utils.format_ratio(
          avgDividendYield
        )}.`,
        iconName: "dollar sign",
        colorProp: "success",
      });

      const topDivYielders = getTopContributors(
        dividendPayers,
        "dividendYield",
        3
      );
      const totalPortfolioDividendYieldFromPayers = dividendPayers.reduce(
        (sum, s) => sum + (s.dividendYield || 0),
        0
      );

      if (
        topDivYielders.length > 0 &&
        totalPortfolioDividendYieldFromPayers > 0
      ) {
        const top3YieldSum = topDivYielders.reduce(
          (sum, s) => sum + (s.dividendYield || 0),
          0
        );
        const top3Contribution =
          (top3YieldSum / totalPortfolioDividendYieldFromPayers) * 100;
        insights.dividends.push({
          key: "dividend_concentration",
          text: `Top ${
            topDivYielders.length
          } dividend payers contribute ${top3Contribution.toFixed(
            1
          )}% of the total yield from dividend-paying stocks in this selection.`,
          iconName: "gem outline",
          colorProp: "warning",
        });
      }
    } else {
      insights.dividends.push({
        key: "no_dividend_payers",
        text: "No stocks in this selection offer a dividend yield.",
        iconName: "info circle",
        colorProp: "info",
      });
    }

    return insights;
  }, [portfolioAnalysisData, sectorAllocationByCount]);

  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState(null);

  useEffect(() => {
    const SIGNIFICANT_MOVE = 0.03;
    const sortedByMarketCap = [...(portfolioAnalysisData || [])]
      .filter((s) => s.marketCap > 0)
      .sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0));
    const topHoldings = sortedByMarketCap.slice(0, 5);
    const movedSymbols = topHoldings
      .filter(
        (s) =>
          typeof s.perfWeek === "number" &&
          Math.abs(s.perfWeek) >= SIGNIFICANT_MOVE
      )
      .map((s) => s.symbol);

    if (movedSymbols.length > 0) {
      setNewsLoading(true);
      setNewsError(null);
      setNews([]);
      Promise.all(movedSymbols.map((symbol) => fetchNews(symbol)))
        .then((allNewsArrays) => {
          const combinedNews = allNewsArrays.flat();
          const sortedNews = combinedNews.sort(
            (a, b) => new Date(b.publishedDate) - new Date(a.publishedDate)
          );
          setNews(sortedNews.slice(0, 5));
        })
        .catch((err) => {
          console.error("Overall news fetching error:", err);
          setNewsError("Failed to load some news articles.");
        })
        .finally(() => setNewsLoading(false));
    } else {
      setNews([]);
    }
  }, [portfolioAnalysisData]);

  const fetchNews = async (symbol) => {
    try {
      const response = await fetch(
        `${FMP_API_ENDPOINTS.STOCK_NEWS()}?${
          API_KEYS.FMP_API_KEY
        }&symbols=${symbol}&limit=2`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch news for ${symbol}`);
      }
      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error(`Error fetching news for ${symbol}:`, error);
      return [];
    }
  };

  if (!portfolioAnalysisData || portfolioAnalysisData.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          minHeight: "680px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
          <PieIcon sx={{ fontSize: 60, color: "action.disabled", mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            No Data for Analysis
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please ensure the screener has results to generate insights.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const insightCategoriesConfig = [
    { title: "Portfolio Composition", key: "composition", icon: <GroupIcon /> },
    {
      title: "Short Term Performance",
      key: "shortTermPerformance",
      icon: <TrendingUp />,
    },
    {
      title: "Long Term Performance",
      key: "longTermPerformance",
      icon: <LineStyleIcon />,
    },
    {
      title: "Valuation & Financial Health",
      key: "valuation",
      icon: <PriceCheckIcon />,
    },
    { title: "Dividend Focus", key: "dividends", icon: <AttachMoney /> },
  ];

  const renderInsightCategoryCard = (category) => {
    const categoryInsights = insightsByCategory[category.key];
    if (!categoryInsights || categoryInsights.length === 0) return null;

    return (
      <Card
        variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxShadow: 2,
          borderRadius: 2,
          "&:hover": { boxShadow: 5 },
        }}
      >
        <CardContent sx={{ flexGrow: 1, p: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
            {React.cloneElement(category.icon, {
              color: "primary",
              sx: { fontSize: "1.8rem" },
            })}
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: "medium", color: "text.primary" }}
            >
              {category.title}
            </Typography>
          </Stack>
          <List dense disablePadding>
            {categoryInsights.map((insight) => (
              <ListItem
                key={insight.key}
                sx={{
                  py: insight.isDivider ? 1 : 0.5,
                  px: 1.5,
                  borderRadius: 1,
                  backgroundColor: insight.isDivider
                    ? "rgba(255, 235, 235, 0.9)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: insight.isDivider
                      ? "rgba(255, 235, 235, 0.9)"
                      : "action.hover",
                  },
                  "&:not(:last-child)": {
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 38,
                    mt: 0.3,
                    color: `${insight.colorProp || "action"}.main`,
                  }}
                >
                  {React.cloneElement(
                    iconMapMUI[insight.iconName] || <InfoIcon />,
                    { fontSize: "small" }
                  )}
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{
                    variant: "body2",
                    lineHeight: 1.3,
                    fontSize: "0.95rem",
                    color: insight.isDivider ? "error.main" : "text.secondary",
                    fontWeight: insight.isDivider ? 600 : 400,
                    sx: insight.isDivider
                      ? {
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }
                      : {},
                  }}
                  primary={insight.text}
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  const totalSectorCount = sectorAllocationByCount.reduce(
    (sum, s) => sum + s.value,
    0
  );

  return (
    <Box
      sx={{
        p: { xs: 1, md: 2 },
        minHeight: { xs: "auto", md: "680px" },
        backgroundColor: "#f9fafb",
        mb: { xs: "80px", md: 0 },
      }}
    >
      {/* First Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Sector Allocation Chart */}
        {sectorAllocationByCount && sectorAllocationByCount.length > 0 && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              variant="outlined"
              sx={{ boxShadow: 3, borderRadius: 2, height: "100%" }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  align="center"
                  gutterBottom
                  sx={{
                    color: "primary.dark",
                    fontWeight: "medium",
                    mt: 1,
                    mb: 1.5,
                  }}
                >
                  <PieIcon sx={{ verticalAlign: "middle", mr: 0.5 }} /> Sector
                  Allocation (by Count)
                </Typography>
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart
                      margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                    >
                      <defs>
                        {sectorAllocationByCount.map((entry, index) => (
                          <radialGradient
                            id={`gradient-sector-insights-${index}`}
                            key={`gradient-sector-insights-${index}`}
                            cx="50%"
                            cy="50%"
                            r="50%"
                            fx="30%"
                            fy="30%"
                          >
                            <stop
                              offset="0%"
                              stopColor={lightenColor(
                                SECTOR_COLORS[index % SECTOR_COLORS.length],
                                25
                              )}
                            />
                            <stop
                              offset="100%"
                              stopColor={
                                SECTOR_COLORS[index % SECTOR_COLORS.length]
                              }
                            />
                          </radialGradient>
                        ))}
                      </defs>
                      <Pie
                        data={sectorAllocationByCount}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderSectorLabel}
                        outerRadius={90}
                        innerRadius={45}
                        dataKey="value"
                        nameKey="name"
                        paddingAngle={2}
                        stroke="#fff"
                        strokeWidth={2}
                      >
                        {sectorAllocationByCount.map((entry, index) => (
                          <Cell
                            key={`cell-sector-${index}`}
                            fill={`url(#gradient-sector-insights-${index})`}
                          />
                        ))}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name) => [
                          `${value} stocks (${name})`,
                          "Count",
                        ]}
                      />
                      <Legend
                        iconType="circle"
                        wrapperStyle={{
                          fontSize: "0.8rem",
                          paddingTop: "10px",
                          lineHeight: "1.3",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Portfolio Composition Chart */}
        {portfolioAnalysisData && portfolioAnalysisData.length > 0 && (
          <Grid item xs={12} sm={6} md={4}>
            <Card
              variant="outlined"
              sx={{ boxShadow: 3, borderRadius: 2, height: "100%" }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  align="center"
                  gutterBottom
                  sx={{
                    color: "primary.dark",
                    fontWeight: "medium",
                    mt: 1,
                    mb: 1.5,
                  }}
                >
                  <GroupIcon sx={{ verticalAlign: "middle", mr: 0.5 }} />{" "}
                  Distribution by Company Size
                </Typography>
                <Box sx={{ height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={[
                        {
                          name: "Large Cap",
                          value: portfolioAnalysisData.filter(
                            (s) => s.companySize === "Large"
                          ).length,
                          fill: "#2ecc40",
                        },
                        {
                          name: "Mid Cap",
                          value: portfolioAnalysisData.filter(
                            (s) => s.companySize === "Mid"
                          ).length,
                          fill: "#ffdc00",
                        },
                        {
                          name: "Small Cap",
                          value: portfolioAnalysisData.filter(
                            (s) => s.companySize === "Small"
                          ).length,
                          fill: "#ff851b",
                        },
                        {
                          name: "Micro Cap",
                          value: portfolioAnalysisData.filter(
                            (s) => s.companySize === "Micro"
                          ).length,
                          fill: "#ff4136",
                        },
                      ]}
                      margin={{ top: 15, right: 10, left: -15, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" fontSize="0.75rem" />
                      <YAxis allowDecimals={false} fontSize="0.75rem" />
                      <RechartsTooltip
                        formatter={(value, name) => [`${value} stocks`, name]}
                        contentStyle={{
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                          border: "none",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: "0.8rem" }} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {[
                          { name: "Large Cap", fill: "#2ecc40" },
                          { name: "Mid Cap", fill: "#ffdc00" },
                          { name: "Small Cap", fill: "#ff851b" },
                          { name: "Micro Cap", fill: "#ff4136" },
                        ].map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                        <Label
                          position="top"
                          content={({ value }) => (
                            <text
                              x={0}
                              y={-10}
                              fill="#666"
                              textAnchor="middle"
                              fontSize={12}
                            >
                              {value}
                            </text>
                          )}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Portfolio Composition Insights */}
        <Grid item xs={12} sm={6} md={4}>
          {renderInsightCategoryCard(
            insightCategoriesConfig.find((cat) => cat.key === "composition")
          )}
        </Grid>
      </Grid>

      {/* Second Row - Performance Insights */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {insightCategoriesConfig
          .filter(
            (category) =>
              category.key === "shortTermPerformance" ||
              category.key === "longTermPerformance"
          )
          .map((category) => {
            const categoryInsights = insightsByCategory[category.key];
            if (!categoryInsights || categoryInsights.length === 0) return null;
            return (
              <Grid item xs={12} md={6} key={category.key}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: 2,
                    borderRadius: 2,
                    "&:hover": { boxShadow: 5 },
                  }}
                >
                  {renderInsightCategoryCard(category)}
                </Card>
              </Grid>
            );
          })}
      </Grid>

      {/* Third Row - Valuation, Dividends, and News */}
      <Grid container spacing={2.5}>
        {/* Valuation & Financial Health */}
        <Grid item xs={12} md={4}>
          {renderInsightCategoryCard(
            insightCategoriesConfig.find((cat) => cat.key === "valuation")
          )}
        </Grid>

        {/* Dividend Focus */}
        <Grid item xs={12} md={4}>
          {renderInsightCategoryCard(
            insightCategoriesConfig.find((cat) => cat.key === "dividends")
          )}
        </Grid>

        {/* News Section */}
        <Grid item xs={12} md={4}>
          {news.length > 0 && (
            <Card
              variant="outlined"
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                boxShadow: 2,
                borderRadius: 2,
                "&:hover": { boxShadow: 5 },
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Typography
                  variant="h6"
                  color="text.primary"
                  gutterBottom
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 500,
                  }}
                >
                  <NewsIcon
                    sx={{
                      verticalAlign: "middle",
                      mr: 1,
                      color: "primary.main",
                    }}
                  />
                  Relevant News
                </Typography>
                {newsLoading && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", my: 2 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
                {newsError && (
                  <Alert severity="error" sx={{ fontSize: "0.8rem", mt: 1 }}>
                    {newsError}
                  </Alert>
                )}
                <List
                  dense
                  disablePadding
                  sx={{ maxHeight: 250, overflowY: "auto" }}
                >
                  {news.map((item, idx) => (
                    <ListItem
                      key={item.url + idx}
                      sx={{
                        py: 0.2,
                        "&:not(:last-child)": {
                          borderBottom: "1px solid #f0f0f0",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Link
                            href={item.url}
                            target="_blank"
                            rel="noopener"
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              "&:hover": {
                                textDecoration: "underline",
                                color: "primary.dark",
                              },
                            }}
                            color="text.primary"
                          >
                            {item.title}
                          </Link>
                        }
                        secondary={`${item.symbol} | ${new Date(
                          item.publishedDate
                        ).toLocaleDateString()}`}
                        primaryTypographyProps={{
                          fontSize: "0.85rem",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        secondaryTypographyProps={{ fontSize: "0.75rem" }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {Object.values(insightsByCategory).every((arr) => arr.length === 0) && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No specific insights could be generated based on the current screener
          data.
        </Alert>
      )}
    </Box>
  );
};

ScreenerInisghts.propTypes = {
  name: PropTypes.string,
  portfolioAnalysisData: PropTypes.array,
  symbolDistributionData: PropTypes.array,
  formatDollarValue: PropTypes.func,
  renderCustomizedLabelWithLines: PropTypes.func,
  BASE_COLORS: PropTypes.array,
  lightenColor: PropTypes.func,
};

ScreenerInisghts.defaultProps = {
  name: "Portfolio",
  portfolioAnalysisData: [],
  symbolDistributionData: [],
  formatDollarValue: (value) => `$${value.toLocaleString()}`,
  renderCustomizedLabelWithLines: (props) => {
    const { cx, cy, midAngle, outerRadius, name, percent } = props;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 28;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (percent * 100 < 2) return null;
    return (
      <text
        x={x}
        y={y}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fill="#444"
        fontSize="11px"
        fontWeight="500"
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  },
  BASE_COLORS: [
    "#FF6384",
    "#36A2EB",
    "#FFCE56",
    "#4BC0C0",
    "#9966FF",
    "#FF9F40",
    "#C9CBCF",
    "#77DD77",
    "#B0E0E6",
    "#FFD700",
  ],
  lightenColor: (color, percent) => {
    let num = parseInt(color.replace("#", ""), 16),
      amt = Math.round(2.55 * percent),
      R = (num >> 16) + amt,
      B = ((num >> 8) & 0x00ff) + amt,
      G = (num & 0x0000ff) + amt;
    return (
      "#" +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
        (G < 255 ? (G < 1 ? 0 : G) : 255)
      )
        .toString(16)
        .slice(1)
    );
  },
};

export default ScreenerInisghts;
